import React, { useCallback, useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import ApplicationModel from '../../../../models/applications/api/ApplicationModel';
import { ui, Group, ActionButton, SelectionCheck } from './ManagementUI';

// Space kept between the bottom of the location list and the bottom of the window
const SIDEBAR_BOTTOM_GAP = 24;
const SIDEBAR_MIN_HEIGHT = 360;

export interface QuickSelectAction {
    label: string;
    onClick: () => void;
    disabled?: boolean;
}

interface LocationSidebarProps {
    title?: string;
    applications: ApplicationModel[];
    visibleApplications: ApplicationModel[];
    selectedAppIds: Set<string>;
    onSelectedAppIdsChange: (selectedAppIds: Set<string>) => void;
    searchTerm: string;
    onSearchTermChange: (searchTerm: string) => void;
    quickActions?: QuickSelectAction[];
    // Secondary line under each location name, e.g. its POS
    getDetail?: (app: ApplicationModel) => string | undefined;
    footer?: React.ReactNode;
    emptyText?: string;
}

// The single list of locations for the Management tab. Every tool works on this selection.
const LocationSidebar: React.FC<LocationSidebarProps> = ({
    title = 'Locations',
    applications,
    visibleApplications,
    selectedAppIds,
    onSelectedAppIdsChange,
    searchTerm,
    onSearchTermChange,
    quickActions = [],
    getDetail,
    footer,
    emptyText = 'No locations found in this organization.'
}) => {
    const selectedCount = applications.filter(app => selectedAppIds.has(app.id)).length;
    const isSearching = searchTerm.trim().length > 0;

    // Keep the toggle handler stable so toggling one row doesn't re-render every row
    const selectionRef = useRef({ selectedAppIds, onSelectedAppIdsChange });
    selectionRef.current = { selectedAppIds, onSelectedAppIdsChange };
    const toggleApp = useCallback((appId: string) => {
        const next = new Set(selectionRef.current.selectedAppIds);
        if (next.has(appId)) {
            next.delete(appId);
        } else {
            next.add(appId);
        }
        selectionRef.current.onSelectedAppIdsChange(next);
    }, []);

    const selectVisible = () => {
        const next = new Set(selectedAppIds);
        visibleApplications.forEach(app => next.add(app.id));
        onSelectedAppIdsChange(next);
    };

    // Fit the sidebar to the space left in the window so the options under the list stay visible.
    // Sets a CSS variable directly to avoid re-rendering the list while scrolling.
    const sidebarRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        let frame = 0;
        const fitToWindow = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const sidebar = sidebarRef.current;
                if (!sidebar) return;
                const available = window.innerHeight - sidebar.getBoundingClientRect().top - SIDEBAR_BOTTOM_GAP;
                sidebar.style.setProperty('--location-sidebar-max-height', `${Math.max(Math.floor(available), SIDEBAR_MIN_HEIGHT)}px`);
            });
        };
        fitToWindow();
        window.addEventListener('resize', fitToWindow);
        // The CMS scrolls inside its own container, so listen for scrolls anywhere on the page
        window.addEventListener('scroll', fitToWindow, true);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', fitToWindow);
            window.removeEventListener('scroll', fitToWindow, true);
        };
    }, []);

    return (
        <Sidebar ref={sidebarRef}>
            <SidebarHeader>
                <SidebarTitle>{title}</SidebarTitle>
                <SidebarCount>{selectedCount > 0 ? `${selectedCount} of ${applications.length} selected` : `${applications.length} total`}</SidebarCount>
            </SidebarHeader>

            <SearchWrapper>
                <SearchIcon viewBox="0 0 16 16" aria-hidden="true">
                    <circle cx="7" cy="7" r="5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                    <line x1="10.8" y1="10.8" x2="14" y2="14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </SearchIcon>
                <SearchInput
                    type="search"
                    aria-label="Search locations"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => onSearchTermChange(e.target.value)}
                />
                {isSearching && (
                    <ClearSearchButton type="button" aria-label="Clear search" onClick={() => onSearchTermChange('')}>×</ClearSearchButton>
                )}
            </SearchWrapper>
            <SearchHint>
                {isSearching
                    ? `${visibleApplications.length} of ${applications.length} match`
                    : 'Tip: use | to match several terms and !"text" to exclude.'}
            </SearchHint>

            <QuickSelect>
                {isSearching ? (
                    <ActionButton variant="plain" text={`Select ${visibleApplications.length} Shown`} onClick={selectVisible} disabled={visibleApplications.length === 0} />
                ) : (
                    <ActionButton variant="plain" text="Select All" onClick={() => onSelectedAppIdsChange(new Set(applications.map(app => app.id)))} disabled={applications.length === 0 || selectedCount === applications.length} />
                )}
                {quickActions.map(action => (
                    <ActionButton key={action.label} variant="plain" text={action.label} onClick={action.onClick} disabled={action.disabled} />
                ))}
                <ActionButton variant="plain" text="Deselect" onClick={() => onSelectedAppIdsChange(new Set())} disabled={selectedAppIds.size === 0} />
            </QuickSelect>

            <ListGroup className="location-list">
                {applications.length === 0 ? (
                    <EmptyList>{emptyText}</EmptyList>
                ) : visibleApplications.length === 0 ? (
                    <EmptyList>No locations match “{searchTerm}”.</EmptyList>
                ) : visibleApplications.map(app => (
                    <LocationListItem
                        key={app.id}
                        appId={app.id}
                        name={app.name}
                        detail={getDetail?.(app)}
                        selected={selectedAppIds.has(app.id)}
                        onToggle={toggleApp}
                    />
                ))}
            </ListGroup>

            {footer}
        </Sidebar>
    );
};

interface LocationListItemProps {
    appId: string;
    name: string;
    detail?: string;
    selected: boolean;
    onToggle: (appId: string) => void;
}

const LocationListItem = React.memo<LocationListItemProps>(({ appId, name, detail, selected, onToggle }) => (
    <LocationRow selected={selected} onClick={() => onToggle(appId)}>
        <SelectionCheck
            type="checkbox"
            aria-label={name}
            checked={selected}
            onChange={() => onToggle(appId)}
            onClick={(e) => e.stopPropagation()}
        />
        <LocationText>
            <LocationName title={name}>{name}</LocationName>
            {detail && <LocationDetail>{detail}</LocationDetail>}
        </LocationText>
    </LocationRow>
));

const Sidebar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    /* Only the location list shrinks to fit; the search and options keep their size */
    & > *:not(.location-list) {
        flex-shrink: 0;
    }

    @media (min-width: 1101px) {
        position: sticky;
        top: 0;
        max-height: var(--location-sidebar-max-height, none);
    }
`;

const SidebarHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 0 4px;
`;

const SidebarTitle = styled.h3`
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: ${ui.label};
`;

const SidebarCount = styled.span`
    font-size: 12px;
    color: ${ui.secondaryLabel};
`;

const SearchWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const SearchIcon = styled.svg`
    position: absolute;
    left: 10px;
    width: 14px;
    height: 14px;
    color: ${ui.secondaryLabel};
    pointer-events: none;
`;

const SearchInput = styled.input`
    width: 100%;
    height: 34px;
    box-sizing: border-box;
    padding: 0 30px 0 32px;
    border: 1px solid transparent;
    border-radius: 9px;
    background: ${ui.fill};
    font-family: inherit;
    font-size: 14px;
    color: ${ui.label};

    &::placeholder {
        color: ${ui.secondaryLabel};
    }

    &::-webkit-search-cancel-button {
        display: none;
    }

    &:focus {
        outline: none;
        background: ${ui.card};
        border-color: ${ui.accent};
        box-shadow: 0 0 0 3px ${ui.accentRing};
    }
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 8px;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: ${ui.tertiaryLabel};
    color: #FFFFFF;
    font-size: 14px;
    line-height: 18px;
    cursor: pointer;
`;

const SearchHint = styled.span`
    padding: 0 4px;
    font-size: 11px;
    color: ${ui.secondaryLabel};
`;

const QuickSelect = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    margin: 0 -4px;
`;

const ListGroup = styled(Group)`
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;

    /* Stacked above the tools on smaller screens, so keep it short enough to reach them */
    @media (max-width: 1100px) {
        max-height: 320px;
    }
`;

const LocationRow = styled.div<{ selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    padding: 10px 14px;
    box-sizing: border-box;
    cursor: pointer;
    background: ${props => props.selected ? ui.accentSoft : 'transparent'};
    /* Lets the browser skip layout and paint for rows scrolled out of view in large organizations */
    content-visibility: auto;
    contain-intrinsic-size: auto 49px;

    /* Selecting hundreds of rows at once shouldn't animate each checkmark */
    input,
    input::after {
        transition: none;
    }

    &:hover {
        background: ${props => props.selected ? ui.accentSoft : 'rgba(118, 118, 128, 0.06)'};
    }
`;

const LocationText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
`;

const LocationName = styled.span`
    font-size: 14px;
    color: ${ui.label};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const LocationDetail = styled.span`
    font-size: 12px;
    color: ${ui.secondaryLabel};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EmptyList = styled.div`
    padding: 32px 16px;
    text-align: center;
    font-size: 13px;
    color: ${ui.secondaryLabel};
`;

export default LocationSidebar;
